// Centralised site content for Articulex
export const CLIENT_NAMES = [
  "Google", "Accenture", "Barclays", "Genpact", "Verizon", "Concentrix",
  "Schneider Electric", "Dell", "Cognizant", "British Council", "PhysicsWallah",
  "TISS", "Salesforce", "Unacademy", "L&T", "IndiGo", "Cvent", "HireMee",
  "Xceedance", "Ksolves", "VIT", "University of Delhi", "IMS Unison",
  "Macmillan Education", "Josh Talks", "BankEdge", "Talgro", "Crossbar",
  "Conscious Chemist"
];

export const STATS = [
  { value: 10000, suffix: "+", label: "Sessions Delivered" },
  { value: 90000, suffix: "+", label: "Professionals Trained" },
  { value: 4.6, suffix: "/5", label: "Average Rating", isRating: true },
  { value: 30, suffix: "+", label: "Enterprise Clients" }
];

export const PROGRAMS = [
  {
    slug: "communication-architecture",
    number: "01",
    title: "Communication Architecture",
    tagline: "For professionals who know what to say but struggle to make it land under pressure.",
    summary:
      "Most professionals are taught what to communicate. Almost none are taught how to architect it. We rebuild the structural backbone of every message — so the right idea, said the right way, lands every single time.",
    learn: [
      "Pyramid Principle and the architecture of executive-grade communication",
      "Framing techniques for high-stakes briefings, presentations and conversations",
      "Storyboarding messages for steering committees, clients and senior leadership",
      "Real-time pressure response — what to say when the room turns"
    ],
    method:
      "70% practice. 30% framework. Every module ends with a live simulation. Recorded. Reviewed. Re-run.",
    outcomes: [
      "Cleaner, more decisive verbal messaging",
      "Sharper written communication for senior audiences",
      "Visible authority in high-stakes conversations"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/5hljttd0_IMG_3618.webp"
  },
  {
    slug: "voice-articulation",
    number: "02",
    title: "Voice, Accent & Articulation",
    tagline: "IPA-informed vocal work that builds the physical foundation of professional credibility.",
    summary:
      "The voice is the first impression — long before the content lands. We use IPA-informed phonetic training to rebuild articulation, pace, tone and resonance, with sustained results over weeks, not days.",
    learn: [
      "International Phonetic Alphabet (IPA) foundations applied to Indian English",
      "Articulator placement, breath support and vocal projection",
      "Pace, pause and prosody — the music of professional speech",
      "Accent neutralisation without losing identity"
    ],
    method:
      "Small-group vocal labs. Recorded baselines. Personalised drill plans. Weekly progress reviews.",
    outcomes: [
      "Crisp, audible articulation in any room",
      "Vocal authority that carries weight",
      "Confidence to speak first, longest and last"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/xa30n28e_IMG_1451.webp"
  },
  {
    slug: "business-writing",
    number: "03",
    title: "Business Writing & Precision",
    tagline: "Writing skills for an era where AI generates the draft and humans must make it matter.",
    summary:
      "AI can produce a paragraph in seconds. It cannot decide what should not be written, what must be cut, and what makes a sentence sound like a decision rather than a hedge. We train professionals to do exactly that.",
    learn: [
      "Editing AI drafts to the standard of senior internal memos",
      "Email economy — saying more, in fewer, sharper lines",
      "Proposal and pitch writing for enterprise audiences",
      "Style discipline: clarity, cadence, conviction"
    ],
    method:
      "Live writing labs. Real drafts from your team. Coaching feedback that compounds over six weeks.",
    outcomes: [
      "Faster, tighter, more persuasive written output",
      "Documents that feel decided, not drafted",
      "An internal standard the team can defend"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/zf4q6w2g_IMG_3796.webp"
  },
  {
    slug: "empathy-influence",
    number: "04",
    title: "Empathy & Influence",
    tagline: "Reading rooms, managing stakeholders, persuading without authority.",
    summary:
      "Most professionals operate on assumed intent. The leaders we work with operate on read intent. We teach the diagnostic skills behind influence — the ability to see what is actually happening in a room, and respond, not react.",
    learn: [
      "Active listening and high-resolution observation",
      "Stakeholder mapping in matrixed and political environments",
      "Persuasion frameworks for cross-functional leadership",
      "Building trust at speed with senior counterparts"
    ],
    method:
      "Roleplay-driven, video-debriefed, coached over three intensives — not a one-day workshop.",
    outcomes: [
      "Higher influence without higher seniority",
      "Better stakeholder navigation in complex projects",
      "Quiet authority built on perceptive judgment"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/5dbmrhl3_7d47d15c-2410-43f3-8dfd-a281c5140166.JPG"
  },
  {
    slug: "negotiation",
    number: "05",
    title: "Negotiation & Difficult Conversations",
    tagline: "Frameworks for high-stakes dialogue — practised until they become instinct.",
    summary:
      "Every professional faces conversations they would rather avoid. We build the muscle to enter them with intent, hold the centre under pressure, and leave with the outcome — and the relationship — intact.",
    learn: [
      "Interest-based negotiation in commercial, cross-team and HR contexts",
      "Tactical empathy and labelling in difficult conversations",
      "BATNA, anchoring, and the choreography of concessions",
      "Repair language for relationships under strain"
    ],
    method:
      "Live, high-pressure simulations against trained counterparts. Recorded, reviewed, and re-run until instinctive.",
    outcomes: [
      "Confidence in conversations most professionals avoid",
      "Better outcomes with clients, peers and reports",
      "Less escalation, more resolution"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/pzm3i0f7_IMG_5498.webp"
  },
  {
    slug: "leadership-presence",
    number: "06",
    title: "Leadership Presence & Problem Solving",
    tagline: "How senior professionals think through ambiguity, decide under pressure, and bring people with them.",
    summary:
      "Leadership presence is not a wardrobe choice. It is the visible by-product of how a person thinks, decides, and carries weight in a room. We rebuild the underlying behaviour — not the surface polish.",
    learn: [
      "Structured problem solving for ambiguous business contexts",
      "Executive presence — body, breath and bearing",
      "Decision frameworks for incomplete information",
      "Bringing people with you: cadence, conviction, communication"
    ],
    method:
      "Cohort-based, case-led, with senior coaches. Designed for managers stepping into leadership and leaders sharpening their second act.",
    outcomes: [
      "Visible presence in senior rooms",
      "Decisive thinking under live pressure",
      "Teams that follow because they believe — not because they must"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/kfvmljrj_8b466d81-86ab-4611-9648-fc23756feb99.JPG"
  },
  {
    slug: "ai-readiness",
    number: "07",
    title: "AI Readiness",
    tagline: "For teams adopting AI tools faster than they are building the judgment to use them well.",
    summary:
      "Most organisations hand their teams AI tools and hope for the best. The result is confident, fluent, occasionally wrong output reaching clients and leadership. This programme builds the human layer around the tools — deciding what AI should touch, instructing it precisely, and evaluating what comes back.",
    learn: [
      "Task selection — which work suits AI assistance, and which demands human judgment",
      "Instruction design — writing briefs and prompts that produce usable output",
      "Output evaluation — spotting confident errors, gaps and hallucinated detail",
      "Responsible use — confidentiality, attribution, and when AI use must be disclosed"
    ],
    method:
      "Scenario-based practice on your team's real tasks. Every exercise ends with an evaluation pass — not just a generated answer.",
    outcomes: [
      "Teams that direct AI instead of deferring to it",
      "A shared standard for checking AI output before it ships",
      "Fewer confident mistakes reaching clients and leadership"
    ],
    assessments: [
      "Participants review a flawed AI-generated client update and flag every claim that needs verification",
      "Teams score real outputs against a shared review checklist — before the programme and after",
      "Scenario judgment tests: five live tasks, and participants must defend which ones should go to AI at all"
    ],
    image:
      "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/tdfbmn4o_Untitled%202.jpg"
  },
  {
    slug: "ai-workflow-automation",
    number: "08",
    title: "AI Workflow Design & Automation",
    tagline: "For operations teams buried in repetitive work that could be systemised — if someone mapped it properly.",
    summary:
      "Automation fails less often on technology than on thinking. Teams automate the wrong step, skip the human check, and inherit a faster way to make the same mistake. This programme teaches your people to map their own workflows, identify what is genuinely automatable, and design the human checks that keep it safe.",
    learn: [
      "Workflow mapping — breaking a process into steps, decisions and handoffs",
      "Automation suitability — what to automate, what to augment, what to keep human",
      "Prompt-to-process — turning repeatable tasks into documented, testable workflows",
      "Human-in-the-loop checks — review points, escalation paths and clear accountability"
    ],
    method:
      "Every participant brings one real recurring workflow. We map it in the room, prototype the automation together, and stress-test where humans must stay in the loop.",
    outcomes: [
      "A mapped, prioritised list of automation opportunities from your own operations",
      "Documented workflows with explicit human checkpoints",
      "Teams who can evaluate new automation ideas without outside help"
    ],
    assessments: [
      "Each participant maps one live workflow and defends where automation helps — and where it harms",
      "Automation-suitability scoring on real processes, reviewed with facilitators",
      "Follow-up review: which mapped opportunities were piloted, and what changed"
    ],
    image:
      "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/lhq8j3k5_IMG_3176.webp"
  }
];

export const TESTIMONIALS = [
  {
    quote: "Jasmeen is an excellent soft skill trainer. She is a determined trainer who makes us more interactive and encourages the team to share their thoughts. It was a great experience.",
    name: "Jayabharathi B",
    title: "Analyst Actuarial Modeling · Empower"
  },
  {
    quote: "Jasmeen is a very good business communication trainer. Her insights on how to communicate with senior leadership, clients and colleagues were really helpful.",
    name: "Ammar Untala",
    title: "Business Systems Analyst · ChainThat"
  },
  {
    quote: "She has a great personality. The way she presents each topic is remarkable. I was impressed by her confidence level. She would be an asset to anyone looking to grow.",
    name: "Sandhya Pradhan",
    title: "EY-Parthenon · SRCC'23"
  },
  {
    quote: "Jasmeen is extremely insightful and explains concepts very beautifully. She will be an absolute asset to any company. Her optimistic approach makes her so unique.",
    name: "Aman Arora",
    title: "Sr. HR Generalist · Best Employee of the Year 2022"
  },
  {
    quote: "I recently attended 3 days training on Employability Skills. She is extremely amicable and has very fluent English. She inhibits qualities like Impeccable, Effective Communication and Clarity.",
    name: "Mohammed Sufiyan Qureshi",
    title: "Site Engineer · Sobha Construction"
  },
  {
    quote: "Jasmeen is sincere and smart working towards her work. She does everything with perfection. She also has good team spirit with amazing leadership qualities.",
    name: "Shekhar Gupta (Capt)",
    title: "Group CEO · 10BestInCity"
  },
  {
    quote: "I have been in the sessions of Jasmeen. It's quite good with respect to confidence building and making sure that you perform better as an orator and sharpen communication skills.",
    name: "Sridhar Rejeti",
    title: "Senior Technical Consultant · Deloitte"
  },
  {
    quote: "Jasmeen is a great instructor, very insightful, sweet and helpful. She will keep you engaged the whole time. She shows how it is possible to learn and have fun together.",
    name: "Mehr Grover",
    title: "Senior Actuarial Analyst · Xceedance"
  }
];

// Featured + supporting recommendations for the homepage.
// Priority order: Ammar → Sridhar → Mehr → Jayabharathi → Sandhya → Aman.
// Text is exactly as supplied — do not rewrite a person's words.
export const HOMEPAGE_RECOMMENDATIONS = {
  featured: {
    quote: "Jasmeen is a very good business communication trainer. Her insights on how to communicate with senior leadership, clients and colleagues were really helpful.",
    name: "Ammar Untala",
    role: "Business Systems Analyst",
    org: "ChainThat"
  },
  supporting: [
    {
      quote: "I have been in the sessions of Jasmeen. It's quite good with respect to confidence building and making sure that you perform better as an orator and sharpen communication skills.",
      name: "Sridhar Rejeti",
      role: "Senior Technical Consultant",
      org: "Deloitte"
    },
    {
      quote: "Jasmeen is a great instructor, very insightful, sweet and helpful. She will keep you engaged the whole time. She shows how it is possible to learn and have fun together.",
      name: "Mehr Grover",
      role: "Senior Actuarial Analyst",
      org: "Xceedance"
    },
    {
      quote: "Jasmeen is an excellent soft skill trainer. She is a determined trainer who makes us more interactive and encourages the team to share their thoughts. It was a great experience.",
      name: "Jayabharathi B",
      role: "Analyst Actuarial Modeling",
      org: "Empower"
    }
  ],
  more: [
    {
      quote: "She has a great personality. The way she presents each topic is remarkable. I was impressed by her confidence level. She would be an asset to anyone looking to grow.",
      name: "Sandhya Pradhan",
      role: "EY-Parthenon",
      org: "SRCC'23"
    },
    {
      quote: "Jasmeen is extremely insightful and explains concepts very beautifully. She will be an absolute asset to any company. Her optimistic approach makes her so unique.",
      name: "Aman Arora",
      role: "Sr. HR Generalist",
      org: "Best Employee of the Year 2022"
    }
  ]
};

// Founder proof strip — Jasmeen's actual roles.

export const AUDIENCES = [
  {
    title: "Large Enterprises & Fortune 500",
    body: "Your L&D calendar is full. Most of the training budget produces completion certificates, not behaviour change. Articulex plugs in as your specialist partner — for the programs where you need it done right."
  },
  {
    title: "IT & Technology Companies",
    body: "Your engineers are exceptional in a lab. The moment they brief a client or present to a steering committee — the gaps show. We bridge them. Communication, presence, and structured thinking built specifically for technical professionals."
  },
  {
    title: "BPOs & Customer-Facing Operations",
    body: "Voice and accent training that goes beyond accent neutralisation. We build the vocal authority, articulation, and conversation management skills that turn agents into professionals clients trust."
  },
  {
    title: "Startups & Founders",
    body: "You have built the product. Now you need the team that can sell it, present it, and represent it at the level it deserves. We work with founding teams and high-growth companies to build capability that scales."
  }
];

export const INSIGHTS = [
  {
    slug: "best-analyst-loses-room",
    category: "COMMUNICATION",
    title: "Why your best analyst loses the room",
    excerpt: "Technical excellence without communication capability is increasingly expensive. Here is what a decade of enterprise programs taught us.",
    readTime: "5 min read",
    body: [
      "Every L&D head we work with has the same story. The strongest technical mind in the team — the one with the cleanest model, the deepest answer, the most defensible recommendation — is also the one who consistently loses the room.",
      "It is not a confidence problem. It is an architecture problem. The information is right. The structure of how it is delivered is wrong.",
      "We see three predictable failure modes: starting at the data instead of the decision, burying the recommendation under context, and answering questions that were not asked. Each is solvable. None are taught by default.",
      "When we rebuild the underlying architecture — through the Pyramid Principle, executive framing, and high-pressure live practice — the same analyst becomes a different professional in a senior room. Same knowledge. Different effect."
    ]
  },
  {
    slug: "voice-gap",
    category: "VOICE & ARTICULATION",
    title: "The voice gap in Indian corporate training",
    excerpt: "Why articulation work is the most neglected — and most high-impact — intervention available to L&D leaders today.",
    readTime: "6 min read",
    body: [
      "Indian corporate training has built strong muscle around content. Frameworks, models, case studies. What it has not built — at scale — is the physical foundation of professional speech.",
      "Most learners enter a session knowing what to say. Their voice betrays them long before their content does. Pace too fast. Articulation imprecise. Vocal placement shallow. The result: high-quality minds judged for low-quality delivery.",
      "Voice work is not vanity. It is the highest-leverage intervention available to L&D leaders today — because it touches every other capability the organisation has already invested in.",
      "IPA-informed articulation training, sustained over weeks, produces results that compound. We have measured it across cohorts. The data is unambiguous."
    ]
  },
  {
    slug: "ai-makes-valuable",
    category: "FUTURE OF WORK",
    title: "What AI makes more valuable, not less",
    excerpt: "Every skill that AI automates increases the premium on the skills it cannot touch. A framework for L&D leaders.",
    readTime: "7 min read",
    body: [
      "The conversation about AI and the workforce is stuck. Half of it is doom — a long list of jobs about to disappear. The other half is denial — a polite assumption that nothing will really change.",
      "Both miss the point. AI is not deleting work. It is redistributing where human value lives.",
      "Every task AI can automate becomes commodity. Every skill AI cannot touch — judgment, persuasion, relational depth, the ability to hold a room and a position simultaneously — becomes premium. The ratio of these two skills inside an organisation is the most important number L&D leaders are not yet tracking.",
      "Our entire program architecture is built around this thesis. The skills we train are precisely the ones whose value increases as AI improves — not despite it."
    ]
  }
];

export const CREDENTIALS = [
  "Executive PG Diploma in OD & Change — TISS Mumbai",
  "BA (Hons) English",
  "10+ Years Enterprise Training",
  "IISE Founder"
];

export const BRAND = {
  wordmark: "articulex",
  wordmarkDotIndex: 2, // position of the 'i' that receives the accent dot
  tagline: "Your new age cap-dev partner",
  descriptor: "Research-led capability development partner",
  positioningTriad: ["Communication", "Leadership", "AI readiness · AI workflow design"],
  endorsement: "Founder, Articulex",
  monogram: "a."
};

// Approved client logo list — kept as-is.
export const PROOF_ORGS = [
  { name: "Google", domain: "google.com" },
  { name: "Accenture", domain: "accenture.com" },
  { name: "Barclays", domain: "barclays.com" },
  { name: "Genpact", domain: "genpact.com" },
  { name: "Dell", domain: "dell.com" },
  { name: "Schneider Electric", domain: "se.com" },
  { name: "British Council", domain: "britishcouncil.org" },
  { name: "PhysicsWallah", domain: "pw.live" },
  { name: "L&T", domain: "larsentoubro.com" },
  { name: "Cvent", domain: "cvent.com" },
  { name: "Xceedance", domain: "xceedance.com" },
  { name: "TISS", domain: "tiss.edu" },
  { name: "Macmillan Education", domain: "macmillaneducation.com" },
  { name: "Conscious Chemist", domain: "consciouschemist.com" }
];

// Homepage credibility strip — static values, no animated zero-start counter.
// Verify with Jasmeen before production deployment.
export const CREDIBILITY = [
  { value: "10+ years", label: "In learning and development" },
  { value: "90,000+", label: "Learners reached" },
  { value: "30+", label: "Organisations and institutions" }
];

// The four capability areas from the branding deck (Slide 5).
export const TERRITORIES = [
  {
    slug: "communication-influence",
    number: "01",
    title: "Communication and influence",
    body: "Client conversations, concise business writing, presentations, stakeholder management and difficult conversations.",
    programs: ["communication-architecture", "voice-articulation", "business-writing", "empathy-influence"],
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/xa30n28e_IMG_1451.webp"
  },
  {
    slug: "leadership-manager-development",
    number: "02",
    title: "Leadership and manager development",
    body: "Feedback, delegation, coaching, accountability and decisions under pressure.",
    programs: ["leadership-presence", "negotiation"],
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/kfvmljrj_8b466d81-86ab-4611-9648-fc23756feb99.JPG"
  },
  {
    slug: "ai-readiness",
    number: "03",
    title: "AI readiness",
    body: "Choosing suitable tasks, giving useful instructions, evaluating outputs and using AI responsibly.",
    programs: ["ai-readiness"],
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/zf4q6w2g_IMG_3796.webp"
  },
  {
    slug: "ai-workflow-automation",
    number: "04",
    title: "AI workflow design and automation",
    body: "Mapping repetitive work, testing suitable automation opportunities and defining human checks.",
    programs: ["ai-workflow-automation"],
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/pzm3i0f7_IMG_5498.webp"
  }
];

// Operating model (Slide 6 + 7 in deck) — kept as four stages for the current site.
export const HOW_THE_WORK = [
  { n: "01", title: "Design around your work", body: "Your roles, work samples and performance priorities shape the programme." },
  { n: "02", title: "Prototype early with SAM", body: "We use SAM to prototype the learning experience and refine it with your stakeholders." },
  { n: "03", title: "70% practice. 30% framework.", body: "Participants attempt the work, receive feedback and try again. The framework supports the task." },
  { n: "04", title: "Measure what changed", body: "Baseline, follow-up evidence, progress and remaining gaps — on a concise client dashboard." }
];

// Replace the old qualitative case studies with the pilot example from Slide 10.
export const CASE_STUDIES = [
  {
    slug: "first-time-managers-pilot",
    sector: "Illustrative pilot",
    title: "A pilot for first-time managers",
    challenge: "Feedback and delegation conversations leave employees unclear about what to do next.",
    focus: "Give feedback using specific observations · Agree an actionable next step · Delegate with clear expectations and follow-up.",
    intervention: "One cohort, three practice sessions, six-week follow-up. Baseline → Practice sessions → Workplace application → Follow-up."
  },
  {
    slug: "problem-pattern-updates",
    sector: "Problem pattern · Communication",
    title: "Client updates with information but no clear recommendation",
    challenge: "Updates contain information but no clear recommendation, leaving decisions unmade.",
    focus: "Explain the issue, recommend an action and make the decision required explicit.",
    intervention: "Practice built around real anonymised update scenarios with facilitator feedback."
  },
  {
    slug: "problem-pattern-ai-outputs",
    sector: "Problem pattern · AI readiness",
    title: "Employees accept convincing AI outputs without checking them",
    challenge: "Teams rely on AI outputs without evaluating accuracy, uncertainty or review responsibility.",
    focus: "Verify important claims, identify uncertainty and decide when human review is necessary.",
    intervention: "Scenario-based practice using real work tasks, with explicit review criteria."
  }
];

export const FOUNDER_ROLES = ["Programme design", "Facilitation", "Communication", "Leadership", "Capability development"];

export const CONTACT = {
  email: "hello@articulex.com",
  altEmail: "jasmeenkaurtalks@gmail.com",
  phone: "9971936197",
  linkedin: "https://linkedin.com/in/jasmeenkaur-trainer",
  instagram: "@jasmeenkaurtalks",
  domain: "articulex.com",
  city: "New Delhi, India"
};

export const HERO_IMAGE = "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/lhq8j3k5_IMG_3176.webp";
// Real facilitation photograph — reused as a considered crop for the founder section.
// Do not swap in a synthetically generated/AI-created portrait.
export const FOUNDER_IMAGE = "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/kfvmljrj_8b466d81-86ab-4611-9648-fc23756feb99.JPG";

// Curated real photographs from Jasmeen's session library.
// No AI-generated / synthetic imagery. Every asset here is a real workshop / talk.
export const SESSION_PHOTOS = {
  bangalore: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/lhq8j3k5_IMG_3176.webp",
  audience: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/kfvmljrj_8b466d81-86ab-4611-9648-fc23756feb99.JPG",
  cognizant: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/5hljttd0_IMG_3618.webp",
  stageLarge: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/xa30n28e_IMG_1451.webp",
  teamMgmt: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/zf4q6w2g_IMG_3796.webp",
  kurtasGroup: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/5dbmrhl3_7d47d15c-2410-43f3-8dfd-a281c5140166.JPG",
  floorActivity: "https://customer-assets-lqy194kg.emergentagent.net/job_capability-builder-9/artifacts/pzm3i0f7_IMG_5498.webp",
  diwaliGroup: "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/tdfbmn4o_Untitled%202.jpg"
};

// Hero rotator — 4 real facilitation photographs, no invented captions.
export const HERO_ROTATOR = [
  SESSION_PHOTOS.bangalore,
  SESSION_PHOTOS.stageLarge,
  SESSION_PHOTOS.cognizant,
  SESSION_PHOTOS.audience
];
export const COLLAGE_IMAGES = [
  "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/zlpu6077_ChatGPT%20Image%20Jun%2027%2C%202026%20at%2007_08_02%20PM.png",
  "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/yf5rtbfi_ChatGPT%20Image%20Jun%2027%2C%202026%20at%2007_10_12%20PM.png",
  "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/zgxhzqzc_ChatGPT%20Image%20Jun%2027%2C%202026%20at%2007_12_16%20PM.png",
  "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/0xxbr3hr_ChatGPT%20Image%20Jun%2027%2C%202026%20at%2007_16_43%20PM.png",
  "https://customer-assets.emergentagent.com/job_capability-builder-9/artifacts/tdfbmn4o_Untitled%202.jpg"
];

// Featured stat-section collage uses 4 photos for a cleaner editorial layout
export const STATS_COLLAGE = COLLAGE_IMAGES.slice(0, 4);

// "How we design" page — the three frameworks referenced in the deck (Slides 6 & 9).
// Method explanation only. No invented client results.
export const METHOD_PAGE = {
  intro:
    "Every Articulex programme is built on three published frameworks — one for how we design, one for how we measure learning, and one for how we frame business value. We use established, research-backed models so you can check our work against the source, not take our word for it.",
  frameworks: [
    {
      n: "01",
      abbr: "SAM",
      name: "Successive Approximation Model",
      use: "How we design",
      body: "Instead of designing a programme behind closed doors and unveiling it at rollout, SAM works in rapid iterations. We prepare with your work samples and priorities, design a rough prototype fast, review it with your stakeholders, and refine — before the full programme is ever built.",
      points: [
        "Preparation: real work samples, roles and performance priorities gathered up front",
        "Iterative design: a working prototype reviewed with your stakeholders — not a 40-page proposal",
        "Iterative development: refined through practice runs before the first cohort enters the room"
      ]
    },
    {
      n: "02",
      abbr: "LTEM",
      name: "Learning-Transfer Evaluation Model",
      use: "How we measure",
      body: "LTEM separates what most training reports — attendance, satisfaction, test scores — from what actually matters: changed behaviour at work. It gives us an honest ladder of evidence, and we tell you plainly which tier a programme is designed to reach.",
      points: [
        "Lower tiers — attendance, completion, knowledge checks — are reported, never celebrated",
        "Higher tiers — decision-making in realistic scenarios, task performance, workplace transfer — are what we design toward",
        "Baselines before the programme, follow-up evidence after: progress is shown against your own starting point"
      ]
    },
    {
      n: "03",
      abbr: "Phillips",
      name: "Phillips ROI Methodology",
      use: "How we frame value",
      body: "The Phillips model organises evaluation into five levels — Reaction, Learning, Application, Impact, and ROI. We build Levels 1–3 into every engagement. Levels 4 and 5 are pursued only when your data can support them — we will not claim a return we cannot evidence.",
      points: [
        "Reaction and Learning: measured in every programme, as standard",
        "Application: follow-up evidence of the skill being used on real work",
        "Impact and ROI: isolated and estimated only where client-side data exists"
      ]
    }
  ],
  closing: "Design iteratively. Measure honestly. Claim only what the evidence holds."
};
